import type { UserDto } from '@api/WhisApi/generated/model/userDto';
import type { SelectOption } from '@components/Select/select.types';
import { getCountryLabel } from '@screens/UserManagement/userManagement.utils';
import type { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import { emailPattern } from './userForm.constants';
import type { UserFormConfig } from './userForm.types';

interface BuildUserFormConfigParams {
  t: TFunction;
  language: SupportedLanguage;
  countryOptions: SelectOption[];
  isCountryLocked: boolean;
  lockedCountry?: UserDto['country'];
}

export const buildUserFormConfig = ({
  t,
  language,
  countryOptions,
  isCountryLocked,
  lockedCountry,
}: BuildUserFormConfigParams): UserFormConfig => {
  const requiredMessage = t('userManagement.errors.required');
  const emailMessage = t('userManagement.errors.email');

  const baseFields: UserFormConfig['fields'] = [
    {
      id: 'firstName',
      type: 'text',
      label: t('userManagement.form.firstName'),
      placeholder: t('userManagement.form.firstNamePlaceholder'),
      autoCapitalize: 'words',
      rules: {
        required: requiredMessage,
      },
    },
    {
      id: 'lastName',
      type: 'text',
      label: t('userManagement.form.lastName'),
      placeholder: t('userManagement.form.lastNamePlaceholder'),
      autoCapitalize: 'words',
      rules: {
        required: requiredMessage,
      },
    },
    {
      id: 'email',
      type: 'text',
      label: t('userManagement.form.email'),
      placeholder: t('userManagement.form.emailPlaceholder'),
      keyboardType: 'email-address',
      autoCapitalize: 'none',
      rules: {
        required: requiredMessage,
        pattern: {
          value: emailPattern,
          message: emailMessage,
        },
      },
    },
    {
      id: 'organization',
      type: 'text',
      label: t('userManagement.form.organization'),
      placeholder: t('userManagement.form.organizationPlaceholder'),
      autoCapitalize: 'words',
      rules: {
        required: requiredMessage,
      },
    },
  ];

  const countryField: UserFormConfig['fields'][number] = isCountryLocked
    ? {
        id: 'countryCode',
        type: 'readonly',
        label: t('userManagement.form.country'),
        displayValue: getCountryLabel(lockedCountry, language),
        rules: {
          required: requiredMessage,
        },
      }
    : {
        id: 'countryCode',
        type: 'select',
        label: t('userManagement.form.country'),
        placeholder: t('userManagement.form.countryPlaceholder'),
        options: countryOptions,
        native: 'web',
        rules: {
          required: requiredMessage,
        },
      };

  return {
    fields: [...baseFields, countryField],
    submitLabel: t('userManagement.actions.save'),
    cancelLabel: t('buttons.cancel'),
    discardConfirmation: {
      title: t('userManagement.modal.discardTitle'),
      description: t('userManagement.modal.discardDescription'),
    },
  };
};
